import type { ReactNode } from "react";
import type { ContentAnimation } from "@veo/types";
import { groupByHeading } from "../lib/richtext";
import { Reveal } from "./motion";

interface TipTapNode {
  type?: string;
  text?: string;
  content?: TipTapNode[];
  attrs?: Record<string, unknown>;
  marks?: { type: string; attrs?: Record<string, unknown> }[];
}

/**
 * Render TipTap JSON.
 *
 * Rendered as React elements rather than via `dangerouslySetInnerHTML`. The
 * content comes from the CMS, and while the API sanitises on write, a renderer
 * that constructs elements directly cannot execute injected markup at all —
 * it is a structural guarantee rather than a filtering one.
 *
 * Unknown node types are skipped silently: a block type added to the editor
 * before this renderer knows about it should degrade, not crash the page.
 */
export function RichText({
  doc,
  className,
  animation = "none",
}: {
  doc: { type: string; content?: unknown[] } | null | undefined;
  className?: string;
  /** Set by the section's appearance settings. See `CONTENT_ANIMATIONS`. */
  animation?: ContentAnimation;
}) {
  if (!doc?.content?.length) return null;

  const nodes = doc.content as TipTapNode[];

  if (animation === "fade") {
    return (
      <Reveal className={className}>
        {nodes.map((node, index) => (
          <RenderNode key={index} node={node} />
        ))}
      </Reveal>
    );
  }

  if (animation === "sequence") {
    return (
      <div className={className}>
        {groupByHeading(nodes).map((group, index) => (
          /*
            The group carries the spacing its heading would have carried. A
            heading's own `mt-8` is cancelled by `first:mt-0` now that it is
            first inside its group, so without this every group after the
            first would sit flush against the one above it.
          */
          <Reveal key={index} className={index > 0 ? "mt-8" : undefined}>
            {group.map((node, nodeIndex) => (
              <RenderNode key={nodeIndex} node={node} />
            ))}
          </Reveal>
        ))}
      </div>
    );
  }

  return (
    <div className={className}>
      {nodes.map((node, index) => (
        <RenderNode key={index} node={node} />
      ))}
    </div>
  );
}

function RenderNode({ node }: { node: TipTapNode }): ReactNode {
  switch (node.type) {
    case "paragraph":
      return (
        <p className="mb-4 text-base leading-relaxed text-ink-700 last:mb-0">
          <Inline nodes={node.content} />
        </p>
      );

    case "heading": {
      const level = Number(node.attrs?.level ?? 2);
      const sizes: Record<number, string> = {
        2: "text-3xl",
        3: "text-2xl",
        4: "text-lg",
      };
      const Tag = `h${Math.min(Math.max(level, 2), 4)}` as "h2" | "h3" | "h4";
      return (
        <Tag className={`mb-3 mt-8 first:mt-0 ${sizes[level] ?? sizes[3]}`}>
          <Inline nodes={node.content} />
        </Tag>
      );
    }

    case "bulletList":
      return (
        <ul className="mb-4 space-y-2 last:mb-0">
          {(node.content ?? []).map((item, index) => (
            <li key={index} className="flex gap-3 text-base leading-relaxed text-ink-700">
              <span aria-hidden className="mt-2.5 size-1.5 shrink-0 rounded-full bg-gold-500" />
              <span>
                {(item.content ?? []).map((child, childIndex) => (
                  <Inline key={childIndex} nodes={child.content} />
                ))}
              </span>
            </li>
          ))}
        </ul>
      );

    case "orderedList":
      return (
        <ol className="mb-4 list-decimal space-y-2 pl-5 last:mb-0 marker:text-gold-600">
          {(node.content ?? []).map((item, index) => (
            <li key={index} className="pl-1 text-base leading-relaxed text-ink-700">
              {(item.content ?? []).map((child, childIndex) => (
                <Inline key={childIndex} nodes={child.content} />
              ))}
            </li>
          ))}
        </ol>
      );

    case "blockquote":
      return (
        <blockquote className="mb-4 border-l-2 border-gold-500 pl-5 font-display text-lg italic text-navy-800">
          {(node.content ?? []).map((child, index) => (
            <RenderNode key={index} node={child} />
          ))}
        </blockquote>
      );

    default:
      return null;
  }
}

/** Inline text with its marks applied. */
function Inline({ nodes }: { nodes?: TipTapNode[] }): ReactNode {
  if (!nodes) return null;

  return (
    <>
      {nodes.map((node, index) => {
        if (node.type !== "text" || !node.text) return null;

        let element: ReactNode = node.text;

        for (const mark of node.marks ?? []) {
          if (mark.type === "bold") element = <strong key={index}>{element}</strong>;
          if (mark.type === "italic") element = <em key={index}>{element}</em>;
          if (mark.type === "link") {
            const href = String(mark.attrs?.href ?? "#");
            // External links open in a new tab with rel protection; internal
            // ones stay in place so navigation history behaves as expected.
            const isExternal = /^https?:\/\//.test(href);
            element = (
              <a
                key={index}
                href={href}
                className="font-medium text-navy-700 underline decoration-gold-500 underline-offset-2 hover:text-navy-900"
                {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              >
                {element}
              </a>
            );
          }
        }

        return <span key={index}>{element}</span>;
      })}
    </>
  );
}
